// AGC: TaskDetail - Task editing page (always in edit mode)
// AGC: Enhanced with inputs section, assignee dropdown, and map preview
// AGC: Click task → Direct edit mode → Save or Cancel
// AGC: Updated with hero gradient header for visual consistency
import React, { useState, useEffect, useRef } from 'react'
import { clampDecimals } from '../../utils'
import { 
  Calendar, 
  MapPin, 
  User, 
  CheckCircle, 
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronLeft,
  Tractor,
  AlertTriangle,
  Loader2,
  Package,
  Plus,
  Map,
  Sprout,
  Droplets,
  SprayCan,
  FlaskConical,
  Wheat,
  Wrench,
  Eye,
  ClipboardList,
  ListTodo,
  Flag
} from 'lucide-react'
import { GlassCard, Button, ProgressBar, TopBar } from '../shared'
import { LotMapPreviewCard } from '../fields/LotMapPreviewCard'
import type { LotMapPreviewData } from '../../types'
import { useQueryClient } from '@tanstack/react-query'
import { getTasks, updateTask, deleteTask } from '../../services/tasks'
import { getActiveInputs, createInputAndInventoryItem } from '../../services/inputsService'
import { fetchTaskTemplates } from '../../services/bomService'
import { getAssignableUsers, type AssigneeOption } from '../../services/assigneesService'
import { useAuth } from '../../hooks/useAuth'
import { useRBAC } from '../../hooks/useRBAC'
import { useLotes, useDeleteConfirm } from '../../context'
import { useTranslation } from '../../hooks/useTranslation'
import { TASK_CATEGORIES, type Screen, type TimelineEntry, type Task, type TaskStatus, type TaskPriority, type TaskInputEntry, type LegacyTaskInputEntry, isLegacyInputEntry, migrateLegacyInputEntry } from '../../types'
import { INPUT_CATEGORIES, type InputMaster } from '../../types/inventory'
import { v4 as uuidv4 } from 'uuid'
import { MACHINE_CATEGORIES, MACHINE_BRANDS, FARM_MACHINES, type MachineCategory } from '../../types/machine'
import type { MachineInstance } from '../../types/machineInstance'
import { getMachines, createMachine } from '../../services/machinesService'
import { getRule, normalizeMachineCategory } from '@droidfarm/shared/activityRules'
import toast from 'react-hot-toast'

interface TaskDetailProps {
  taskId: string
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: TaskDetail - Direct edit mode when clicking a task
 * AGC: Cancel → Back to task list (discard changes)
 * AGC: Save → Save changes and back to task list
 * AGC: Now includes: inputs management, assignee dropdown, map preview
 */
export const TaskDetail: React.FC<TaskDetailProps> = ({ taskId, onNavigate, onBack }) => {
  const { getAuthToken, user } = useAuth()
  const rbac = useRBAC()
  const { plots: lotes } = useLotes()  // AGC: Rename plots to lotes for consistency
  const { t } = useTranslation()
  const { confirmBeforeDelete, requestConfirmDelete } = useDeleteConfirm()
  const queryClient = useQueryClient()

  // AGC: Ref for progress section auto-scroll
  const progressRef = useRef<HTMLDivElement>(null)
  const [showProgressHint, setShowProgressHint] = useState(false)

  // AGC: State
  const [task, setTask] = useState<Task | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  // AGC: Edit form state
  const [editForm, setEditForm] = useState<Partial<Task>>({})
  
  // AGC: State for tracking original due date (for cascade detection)
  const [originalDueDate, setOriginalDueDate] = useState<string | null>(null)
  
  // AGC: State for cascade dialog
  const [showCascadeDialog, setShowCascadeDialog] = useState(false)
  const [subsequentTasksCount, setSubsequentTasksCount] = useState(0)
  const [dateDeltaDays, setDateDeltaDays] = useState(0)
  
  // AGC: Dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false)
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  // AGC: T3 — 'either' mode radio: 'self-propelled' | 'trator-implement'
  const [machineryMode, setMachineryMode] = useState<'self-propelled' | 'trator-implement'>('self-propelled')
  // AGC: T3 — slot-indexed dropdown open state for combo/either pickers (replaces single showMachineDropdown)
  const [openSlot, setOpenSlot] = useState<number | null>(null)
  const [showLotDropdown, setShowLotDropdown] = useState(false)
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false)
  const [showInputDropdown, setShowInputDropdown] = useState(false)
  // AGC: "Cadastrar novo produto" modal state
  const [showNewInputModal, setShowNewInputModal] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [registerForm, setRegisterForm] = useState({ name: '', category: 'other', unit: 'L', costPerUnit: 0, initialQuantity: 0, minimumStock: 0 })
  // AGC: "Cadastrar nova máquina" modal state
  const [showNewMachineModal, setShowNewMachineModal] = useState(false)
  const [isRegisteringMachine, setIsRegisteringMachine] = useState(false)
  const [machineForm, setMachineForm] = useState({ name: '', category: 'trator' as MachineCategory, catalogId: '', brand: '', model: '', year: '' })

  // AGC: NEW - Available inputs and assignees from Firebase
  const [availableInputs, setAvailableInputs] = useState<InputMaster[]>([])
  const [availableAssignees, setAvailableAssignees] = useState<AssigneeOption[]>([])
  const [isLoadingInputs, setIsLoadingInputs] = useState(false)
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false)
  
  // AGC: NEW - For adding new input entry (supports both catalog and custom inputs)
  const [newInputEntry, setNewInputEntry] = useState<{
    inputId: string
    quantity: string
    isCustom: boolean
    customName: string
    customUnit: string
  }>({ inputId: '', quantity: '', isCustom: false, customName: '', customUnit: 'L' })
  
  // AGC: Get current lot for area calculations
  const currentLot = (lotes || []).find(l => l.id === editForm.plotId || l.id === task?.plotId)
  const lotAreaHa = currentLot?.area || 1 // AGC: Default to 1 ha if not available

  // AGC: Permission resolution (v3.9.3 RBAC-aware):
  // AGC:   - Farm owner: full edit, always
  // AGC:   - Anyone with canEditTask (manager/agronomist): full edit
  // AGC:   - Task creator (any role): full edit of their own task (creator bonus)
  // AGC:   - Worker (canUpdateTaskStatus=true): can change status/progress even when not creator
  const isTaskCreator = task?.createdBy === user?.uid || !task?.createdBy
  const isFarmOwner = rbac.isOwner
  const hasEditPerm = !!rbac.permissions?.canEditTask
  const hasStatusPerm = !!rbac.permissions?.canUpdateTaskStatus
  // AGC: canEdit gates title/description/priority/assignee/machine/inputs/etc.
  const canEdit = isFarmOwner || hasEditPerm || isTaskCreator
  // AGC: canUpdateStatus gates the status dropdown + progress
  const canUpdateStatus = canEdit || hasStatusPerm

  // AGC: Load task from API
  useEffect(() => {
    const loadTask = async () => {
      try {
        setIsLoading(true)
        const token = await getAuthToken()
        const tasks = await getTasks(token)
        const foundTask = tasks.find(t => t.id === taskId)
        
        if (foundTask) {
          setTask(foundTask)
          setEditForm(foundTask)
          setOriginalDueDate(foundTask.dueDate)
        }
      } catch (error) {
        console.error('AGC: Error loading task:', error)
        toast.error('Erro ao carregar tarefa')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadTask()
  }, [taskId, getAuthToken])

  // AGC: Load available inputs from admin catalog
  useEffect(() => {
    const loadInputs = async () => {
      try {
        setIsLoadingInputs(true)
        const token = await getAuthToken()
        const inputs = await getActiveInputs(token)
        setAvailableInputs(inputs)
      } catch (error) {
        console.error('AGC: Error loading inputs:', error)
      } finally {
        setIsLoadingInputs(false)
      }
    }
    
    loadInputs()
  }, [getAuthToken])

  // AGC: v3.12.2 — Load the user's real machinery fleet for the Máquina picker.
  // AGC: Replaces the hardcoded FARM_MACHINES catalog so the picker reflects what the
  // AGC: user actually owns. Filtered by task category below via machineryMatchesCategory.
  const [userMachines, setUserMachines] = useState<MachineInstance[]>([])
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const token = await getAuthToken()
        const list = await getMachines(token)
        if (!cancelled) setUserMachines(Array.isArray(list) ? list : [])
      } catch (e) {
        console.warn('AGC: [TaskDetail] load machines failed', e)
        if (!cancelled) setUserMachines([])
      }
    }
    load()
    return () => { cancelled = true }
  }, [getAuthToken])

  // AGC: T4 — Ensure-diesel: when a task loads without a diesel input, look up the
  // AGC: crop template and prepend a diesel entry so the inputs table shows diesel L/ha.
  // AGC: Only runs when currentLot is available (needs area + cropType).
  // AGC: Runs after both `task` and `currentLot` are resolved (lotes come from context).
  useEffect(() => {
    if (!task || !currentLot) return
    // AGC: Skip completed tasks — no need to backfill.
    if (task.status === 'completed') return
    // AGC: Skip if diesel input already present (idempotent).
    const hasDiesel = (task.inputs || []).some(i => {
      const id = 'inputId' in i ? i.inputId : ''
      return id === 'diesel' || (i.inputName || '').toLowerCase().includes('diesel')
    })
    if (hasDiesel) return

    const cropId = currentLot.cropType as string
    if (!cropId) return

    let cancelled = false
    const ensureDiesel = async () => {
      try {
        const token = await getAuthToken()
        const templates = await fetchTaskTemplates(cropId, token ?? undefined)
        if (cancelled) return

        // AGC: Find the template whose title matches this task (normalised comparison).
        const normalise = (s: string) => s.toLowerCase().trim()
        const match = templates.find(tpl =>
          normalise(tpl.title) === normalise(task.title)
        )
        if (!match) return

        const dieselTpl = match.inputs.find(inp =>
          inp.inputId === 'diesel' || normalise(inp.inputName).includes('diesel')
        )
        if (!dieselTpl) return

        const areaHa = currentLot.area || lotAreaHa
        const newDiesel = {
          inputId: dieselTpl.inputId,
          inputName: dieselTpl.inputName,
          category: dieselTpl.category,
          unit: dieselTpl.unit,
          quantityPerHa: dieselTpl.quantityPerHa,
          totalQuantity: parseFloat((dieselTpl.quantityPerHa * areaHa).toFixed(4)),
          inputSnapshot: {
            name: dieselTpl.inputName,
            category: dieselTpl.category,
            unit: dieselTpl.unit,
            capturedAt: new Date().toISOString(),
          },
          isCustom: false,
          autoAdded: true,  // AGC: marker for T4 client-side ensure-diesel prepend
        } as import('../../types').TaskInputEntry & { autoAdded: boolean }

        // AGC: Prepend the diesel row so it appears first in the inputs table.
        setEditForm(prev => ({
          ...prev,
          inputs: [newDiesel, ...(prev.inputs || [])],
        }))
        console.log('AGC: [TaskDetail] ensure-diesel prepended diesel row from template', cropId)
      } catch (err) {
        console.warn('AGC: [TaskDetail] ensure-diesel fetch failed', err)
      }
    }
    ensureDiesel()
    return () => { cancelled = true }
  // AGC: Intentionally exclude editForm to avoid re-running after our own setEditForm.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, currentLot, getAuthToken])

  // AGC: Load available assignees (Me + team members)
  // AGC: Also includes currently assigned collaborator if not in team (for editing existing tasks)
  // AGC: Refreshes when team member is created (listens to 'team-member-created' event)
  useEffect(() => {
    const loadAssignees = async () => {
      try {
        setIsLoadingAssignees(true)
        const assignees = await getAssignableUsers(user)
        
        // AGC: If task has an assigned collaborator that's not in the available list, add it
        // AGC: This ensures the assigned collaborator appears in the dropdown when editing
        if (task?.assignedToId && task.assignedToId !== user?.uid) {
          const assignedCollaboratorExists = assignees.some(a => a.uid === task.assignedToId)
          
          if (!assignedCollaboratorExists && task.assignedTo) {
            // AGC: Add the assigned collaborator to the list (even if not in current team)
            assignees.push({
              id: task.assignedToId,
              uid: task.assignedToId,
              displayName: task.assignedTo,
              avatar: task.assignedToAvatar,
              isCurrentUser: false,
              role: undefined,
              subtype: undefined,
            })
            console.log('✅ AGC: Added assigned collaborator to dropdown:', task.assignedTo)
          }
        }
        
        setAvailableAssignees(assignees)
      } catch (error) {
        console.error('AGC: Error loading assignees:', error)
      } finally {
        setIsLoadingAssignees(false)
      }
    }
    
    if (user) {
      loadAssignees()
      
      // AGC: Listen for team member creation events to refresh assignees list
      const handleTeamMemberCreated = async (event: CustomEvent) => {
        console.log('🔄 AGC: Team member created event received - refreshing assignees list', event.detail)
        // AGC: Wait a moment for Firestore to sync before refreshing
        await new Promise(resolve => setTimeout(resolve, 500))
        await loadAssignees()
      }
      
      window.addEventListener('team-member-created', handleTeamMemberCreated as EventListener)
      
      return () => {
        window.removeEventListener('team-member-created', handleTeamMemberCreated as EventListener)
      }
    }
  }, [user, task?.assignedToId, task?.assignedTo, task?.assignedToAvatar])

  // AGC: Auto-scroll to progress bar when status changes to in-progress
  useEffect(() => {
    if (editForm.status === 'in-progress' && task && task.status !== 'in-progress') {
      setShowProgressHint(true)
      setTimeout(() => {
        progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 150)
      const timer = setTimeout(() => setShowProgressHint(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [editForm.status, task?.status])

  // AGC: Calculate date difference in days
  const calculateDeltaDays = (oldDate: string, newDate: string): number => {
    const old = new Date(oldDate)
    const newD = new Date(newDate)
    const diffMs = newD.getTime() - old.getTime()
    return Math.round(diffMs / (1000 * 60 * 60 * 24))
  }

  // AGC: Check if there are subsequent tasks that might need to be shifted
  const checkForSubsequentTasks = async (): Promise<number> => {
    if (!task?.plotId || task.order === undefined) return 0
    
    try {
      const token = await getAuthToken()
      const allTasks = await getTasks(token)
      
      const subsequent = allTasks.filter(t => 
        t.plotId === task.plotId && 
        t.order !== undefined && 
        t.order > (task.order || 0)
      )
      
      return subsequent.length
    } catch (error) {
      console.error('AGC: Error checking subsequent tasks:', error)
      return 0
    }
  }

  // AGC: Handle adding an input entry (supports both catalog and custom inputs)
  const handleAddInput = () => {
    const quantity = parseFloat(newInputEntry.quantity)
    
    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('tasks.enterValidQuantity'))
      return
    }
    
    let newEntry: TaskInputEntry
    
    if (newInputEntry.isCustom) {
      // AGC: Custom input - not from catalog
      if (!newInputEntry.customName.trim()) {
        toast.error(t('tasks.enterInputName'))
        return
      }
      
      newEntry = {
        inputId: `custom-${uuidv4()}`,
        inputName: newInputEntry.customName.trim(),
        quantityPerHa: clampDecimals(quantity / lotAreaHa),
        totalQuantity: clampDecimals(quantity),
        unit: newInputEntry.customUnit || 'un',
        inputSnapshot: {
          name: newInputEntry.customName.trim(),
          category: 'other',
          unit: newInputEntry.customUnit || 'un',
          capturedAt: new Date().toISOString(),
        },
        isCustom: true,
        isDiscontinued: false,
      }
    } else {
      // AGC: Catalog input - from InputMaster
      if (!newInputEntry.inputId) {
        toast.error(t('tasks.selectInputAndQuantity'))
        return
      }
      
      const selectedInput = availableInputs.find(i => i.id === newInputEntry.inputId)
      if (!selectedInput) {
        toast.error(t('tasks.inputNotFound'))
        return
      }
      
      newEntry = {
        inputId: selectedInput.id,
        inputName: selectedInput.name,
        quantityPerHa: clampDecimals(quantity / lotAreaHa),
        totalQuantity: clampDecimals(quantity),
        unit: selectedInput.unit || 'un',
        inputSnapshot: {
          name: selectedInput.name,
          category: selectedInput.category || 'other',
          unit: selectedInput.unit || 'un',
          capturedAt: new Date().toISOString(),
        },
        isCustom: false,
        isDiscontinued: !selectedInput.isActive,
      }
    }
    
    const currentInputs = editForm.inputs || []
    setEditForm(prev => ({
      ...prev,
      inputs: [...currentInputs, newEntry]
    }))
    
    // AGC: Reset form
    setNewInputEntry({ inputId: '', quantity: '', isCustom: false, customName: '', customUnit: 'L' })
    setShowInputDropdown(false)
  }

  // AGC: Handle removing an input entry
  const handleRemoveInput = (index: number) => {
    const currentInputs = editForm.inputs || []
    setEditForm(prev => ({
      ...prev,
      inputs: currentInputs.filter((_, i) => i !== index)
    }))
  }

  // AGC: Register new product → admin catalog + inventory, then auto-select in input form
  const handleRegisterNewInput = async () => {
    if (!registerForm.name.trim()) return
    setIsRegistering(true)
    try {
      const token = await getAuthToken()
      const result = await createInputAndInventoryItem({
        name: registerForm.name.trim(),
        category: registerForm.category,
        unit: registerForm.unit,
        costPerUnit: registerForm.costPerUnit,
        initialQuantity: registerForm.initialQuantity,
        minimumStock: registerForm.minimumStock,
      }, token)
      if (result) {
        // AGC: Refresh available inputs to include the new catalog entry
        const refreshed = await getActiveInputs(token)
        setAvailableInputs(refreshed)
        // AGC: Auto-select the new input in the form
        setNewInputEntry(prev => ({ ...prev, inputId: result.inputId, isCustom: false }))
        toast.success(`${registerForm.name} cadastrado!`, { icon: '✅' })
      }
      setShowNewInputModal(false)
      setRegisterForm({ name: '', category: 'other', unit: 'L', costPerUnit: 0, initialQuantity: 0, minimumStock: 0 })
    } catch (err) {
      toast.error('Erro ao cadastrar produto')
    } finally {
      setIsRegistering(false)
    }
  }

  // AGC: Register new machine → creates via API, refreshes list, auto-selects
  const handleRegisterNewMachine = async () => {
    if (!machineForm.name.trim()) return
    setIsRegisteringMachine(true)
    try {
      const token = await getAuthToken()
      const result = await createMachine({
        name: machineForm.name.trim(),
        category: machineForm.category,
        catalogId: machineForm.catalogId || undefined,
        brand: machineForm.brand || undefined,
        model: machineForm.model || undefined,
        year: machineForm.year ? parseInt(machineForm.year) : undefined,
        horimeterHours: 0,
        status: 'available',
      }, token)
      if (result) {
        const refreshed = await getMachines(token)
        setUserMachines(refreshed)
        // AGC: Auto-select the new machine in the task
        const newMachine = refreshed.find((m: { name: string }) => m.name === machineForm.name.trim())
        if (newMachine) {
          setEditForm(prev => ({
            ...prev,
            machinery: [{ id: newMachine.id, name: newMachine.name, category: newMachine.category }],
          }))
        }
        toast.success(`${machineForm.name} cadastrada!`, { icon: '🚜' })
      }
      setShowNewMachineModal(false)
      setMachineForm({ name: '', category: 'trator', catalogId: '', brand: '', model: '', year: '' })
    } catch (err) {
      toast.error('Erro ao cadastrar máquina')
    } finally {
      setIsRegisteringMachine(false)
    }
  }

  // AGC: Handle save with cascade logic
  const handleSave = async (updateStrategy?: 'keep' | 'shift') => {
    if (!task) return
    
    // AGC: Permission check — allow save if user can either edit fields or update status
    // AGC: Backend enforces field-level gating (canEditTask vs canUpdateTaskStatus via denylist)
    if (!canEdit && !canUpdateStatus) {
      toast.error(t('tasks.cannotEditNotOwner'))
      return
    }
    
    // AGC: Check if date changed and if we need to show cascade dialog
    const newDueDate = editForm.dueDate?.split('T')[0]
    const oldDueDate = originalDueDate?.split('T')[0]
    
    if (newDueDate && oldDueDate && newDueDate !== oldDueDate && !updateStrategy) {
      const count = await checkForSubsequentTasks()
      
      if (count > 0) {
        const delta = calculateDeltaDays(oldDueDate, newDueDate)
        setSubsequentTasksCount(count)
        setDateDeltaDays(delta)
        setShowCascadeDialog(true)
        return
      }
    }
    
    setIsSaving(true)
    try {
      const token = await getAuthToken()
      
      const newTimelineEntry: TimelineEntry = {
        id: `entry-${Date.now()}`,
        date: new Date().toISOString(),
        status: editForm.status || task.status,
        user: user?.displayName || 'Usuário',
        notes: updateStrategy === 'shift' 
          ? `Tarefa atualizada (tarefas subsequentes ajustadas em ${dateDeltaDays > 0 ? '+' : ''}${dateDeltaDays} dias)`
          : 'Tarefa atualizada'
      }
      
      const updatedTask = {
        ...editForm,
        timeline: [...(task.timeline || []), newTimelineEntry],
        updatedAt: new Date().toISOString()
      }
      
      await updateTask(taskId, updatedTask, token, updateStrategy)
      
      toast.success('Tarefa salva!', { icon: '✅' })

      // AGC: Invalidate tasks + inventory cache so list and stock refetch immediately
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] })
      window.dispatchEvent(new CustomEvent('task-updated'))

      onBack()
    } catch (error) {
      console.error('AGC: Error saving task:', error)
      toast.error('Erro ao salvar tarefa')
    } finally {
      setIsSaving(false)
      setShowCascadeDialog(false)
    }
  }

  // AGC: Handle cascade dialog choice
  const handleCascadeChoice = (strategy: 'keep' | 'shift') => {
    setShowCascadeDialog(false)
    handleSave(strategy)
  }

  // AGC: Handle delete
  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      const token = await getAuthToken()
      await deleteTask(taskId, token)
      toast.success('Tarefa excluída', { icon: '🗑️' })
      
      window.dispatchEvent(new CustomEvent('task-deleted'))
      console.log('📤 AGC: Dispatched task-deleted event')
      
      onBack()
    } catch (error) {
      console.error('AGC: Error deleting task:', error)
      toast.error('Erro ao excluir tarefa')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  // AGC: Cancel - goes back without saving
  const handleCancel = () => {
    onBack()
  }

  // AGC: Get lot coordinates for map preview (with safety check)
  // AGC: currentLot is already defined above for area calculations
  const hasCoordinates = currentLot?.coordinates && currentLot.coordinates.length > 2

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-accent)]" />
      </div>
    )
  }

  // AGC: Task not found
  if (!task) {
    return (
      <div className="min-h-screen app-bg">
        <TopBar title={t('tasks.task')} onBack={handleCancel} />
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <AlertTriangle className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('tasks.taskNotFound')}</p>
          <Button variant="secondary" className="mt-4" onClick={handleCancel}>
            {t('common.back')}
          </Button>
        </div>
      </div>
    )
  }

  const categoryInfo = TASK_CATEGORIES.find(c => c.id === editForm.category || c.id === task.category)
  const selectedAssignee = availableAssignees.find(a => a.uid === editForm.assignedToId)

  // AGC: Status options - using translations
  const statusOptions: { value: TaskStatus; label: string; color: string }[] = [
    { value: 'pending', label: t('tasks.statusPending'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
    { value: 'in-progress', label: t('tasks.statusInProgress'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    { value: 'completed', label: t('tasks.statusCompleted'), color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  ]

  // AGC: Priority options with Lucide icons - using translations
  const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
    { value: 'high', label: t('tasks.priorityHigh'), color: 'text-red-500' },
    { value: 'medium', label: t('tasks.priorityMedium'), color: 'text-amber-500' },
    { value: 'low', label: t('tasks.priorityLow'), color: 'text-green-500' },
  ]

  // AGC: Task category PNG icon mapping
  const taskIconImgMap: Record<string, string> = {
    plantio: '/Machinery_icons/task/Plantio.png',
    irrigacao: '/Machinery_icons/task/irrigacao.png',
    pulverizacao: '/Machinery_icons/task/pulverizacao.png',
    adubacao: '/Machinery_icons/task/adbubacao.png',
    colheita: '/Machinery_icons/task/colheita.png',
    manutencao: '/Machinery_icons/task/Manuntencao.png',
    monitoramento: '/Machinery_icons/task/scout.png',
    outros: '/Machinery_icons/task/outros.png',
  }

  const getTaskCategoryIcon = (categoryId: string | undefined): React.ComponentType<{ className?: string }> => {
    switch (categoryId) {
      case 'plantio': return Sprout
      case 'irrigacao': return Droplets
      case 'pulverizacao': return SprayCan
      case 'adubacao': return FlaskConical
      case 'colheita': return Wheat
      case 'manutencao': return Wrench
      case 'monitoramento': return Eye
      case 'outros': return ClipboardList
      default: return ListTodo
    }
  }

  const currentCategory = editForm.category || task?.category || ''
  const TaskCategoryIcon = getTaskCategoryIcon(currentCategory)
  const taskCategoryImg = taskIconImgMap[currentCategory]

  return (
    <div className="min-h-screen app-bg pb-24">
      {/* AGC: Hero Header - Solid green gradient */}
      <header
        className="px-4 pt-4 pb-4 rounded-b-2xl relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
      >
        {/* AGC: Decorative background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
          />
        </div>

        {/* AGC: Top row - Back button and delete button */}
        <div className="flex items-center justify-between mb-3 relative z-10">
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">{t('common.back')}</span>
          </button>
          
          {canEdit && (
            <button
              onClick={() => {
                if (confirmBeforeDelete) {
                  requestConfirmDelete({ onConfirm: handleDelete })
                } else {
                  setShowDeleteConfirm(true)
                }
              }}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <Trash2 className="w-5 h-5 text-white" />
            </button>
          )}
        </div>

        {/* AGC: Main header row with icon and title */}
        <div className="flex items-start gap-3 relative z-10">
          {/* AGC: Task type icon in translucent container */}
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            {taskCategoryImg ? <img src={taskCategoryImg} alt="" className="w-7 h-7 object-contain" /> : <TaskCategoryIcon className="w-6 h-6 text-white" />}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* AGC: Task title - editable */}
            <input
              type="text"
              value={editForm.title || ''}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              className="text-lg font-bold text-white bg-transparent border-b border-white/30 focus:border-white focus:outline-none w-full placeholder-white/50"
              placeholder={t('tasks.taskTitle')}
              disabled={!canEdit}
            />
            <p className="text-white/70 text-sm mt-1">{categoryInfo?.name || 'Outros'}</p>
          </div>
        </div>

        {/* AGC: Progress section in header — ref for auto-scroll on status change */}
        <div ref={progressRef} className="mt-4 relative z-10">
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-white/70">{t('tasks.progress')}</span>
            <span className="font-medium text-white">{editForm.progress || 0}%</span>
          </div>
          {showProgressHint && (
            <div className="text-center mb-2 animate-pulse">
              <span className="text-xs text-white/90 bg-white/20 px-3 py-1 rounded-full">
                {t('tasks.adjustProgress')}
              </span>
            </div>
          )}
          <div className="relative h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-white/80 rounded-full transition-all duration-300"
              style={{ width: `${editForm.progress || 0}%` }}
            />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={editForm.progress || 0}
            onChange={(e) => {
              if (!canEdit) return
              const progress = parseInt(e.target.value)
              const status = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'pending'
              setEditForm(prev => ({ ...prev, progress, status: status as TaskStatus }))
            }}
            className="w-full h-6 opacity-0 absolute top-0 cursor-pointer"
            style={{ marginTop: '-12px' }}
            disabled={!canEdit}
          />
        </div>
      </header>

      {/* AGC: Edit not allowed warning — show only when user can neither edit fields nor update status */}
      {!canEdit && !canUpdateStatus && (
        <div className="mx-4 mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            {t('tasks.cannotEditNotOwner')}
          </p>
        </div>
      )}

      {/* AGC: Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4">
          <GlassCard className="max-w-sm w-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('tasks.deleteTaskTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('tasks.deleteTaskBody')}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  fullWidth
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="!bg-red-600 hover:!bg-red-700"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('common.delete')
                  )}
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {/* AGC: Cascade Scheduling Dialog */}
      {showCascadeDialog && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4">
          <GlassCard className="max-w-sm w-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('tasks.cascadeTitle')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {t('tasks.cascadeMessage')} <strong>{dateDeltaDays > 0 ? '+' : ''}{dateDeltaDays} {t('tasks.cascadeDays')}</strong>.
              </p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mb-6">
                {t('tasks.cascadeExistTasks')} <strong>{subsequentTasksCount}</strong> {t('tasks.cascadeSubsequentTasks')}.
                {' '}{t('tasks.cascadeAskAdjust')}
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  fullWidth
                  onClick={() => handleCascadeChoice('shift')}
                  disabled={isSaving}
                  className="!bg-blue-600 hover:!bg-blue-700"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    `${t('tasks.cascadeYesShift')} ${subsequentTasksCount} ${t('tasks.cascadeTasks')}`
                  )}
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => handleCascadeChoice('keep')}
                  disabled={isSaving}
                >
                  {t('tasks.cascadeNoKeep')}
                </Button>
                <button
                  onClick={() => setShowCascadeDialog(false)}
                  className="text-gray-500 text-sm hover:text-gray-700 dark:hover:text-gray-300"
                  disabled={isSaving}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {/* AGC: Task Details */}
      <div className="px-4 py-4 space-y-3">
        {/* AGC: Description */}
        <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('tasks.description')}</h3>
          <textarea
            value={editForm.description || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-gray-900 dark:text-white"
            rows={3}
            placeholder={t('tasks.descriptionPlaceholder')}
            disabled={!canEdit}
          />
        </div>

        {/* AGC: Inputs Section - Clean Table Layout */}
        <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          {/* AGC: Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Package className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('tasks.inputs')}
              </h3>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
              {(editForm.inputs || []).length} {t('tasks.items')}
            </span>
          </div>
          
          {/* AGC: Inputs Table */}
          {(editForm.inputs || []).length > 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden mb-3">
              {/* AGC: Table Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <div className="col-span-5">{t('tasks.inputName')}</div>
                <div className="col-span-2 text-right">{t('tasks.qty')}</div>
                <div className="col-span-2">{t('tasks.unit')}</div>
                <div className="col-span-2 text-right">/ha</div>
                <div className="col-span-1"></div>
              </div>
              
              {/* AGC: Table Body */}
              {(editForm.inputs || []).map((input, index) => {
                const isDiscontinued = (input as TaskInputEntry).isDiscontinued === true
                const isCustom = (input as TaskInputEntry).isCustom === true
                const totalQty = 'totalQuantity' in input ? input.totalQuantity : (input as LegacyTaskInputEntry).quantity
                const perHaQty = 'quantityPerHa' in input ? input.quantityPerHa : null
                
                return (
                  <div 
                    key={index} 
                    className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${
                      isDiscontinued ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
                    }`}
                  >
                    {/* AGC: Input Name with badges */}
                    <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                      <span className="text-sm text-gray-900 dark:text-white truncate">{input.inputName}</span>
                      {isCustom && (
                        <span className="flex-shrink-0 px-1 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                          {t('tasks.custom')}
                        </span>
                      )}
                      {isDiscontinued && (
                        <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* AGC: Quantity — clamped to 2 decimal places */}
                    <div className="col-span-2 text-right">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{clampDecimals(Number(totalQty) || 0)}</span>
                    </div>
                    
                    {/* AGC: Unit */}
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{input.unit}</span>
                    </div>
                    
                    {/* AGC: T4 — Per Hectare: editable when canEdit, read-only span otherwise. */}
                    {/* AGC: Changing L/ha recomputes totalQuantity = quantityPerHa × lotAreaHa. */}
                    <div className="col-span-2 text-right">
                      {canEdit && 'quantityPerHa' in input ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={perHaQty !== null ? clampDecimals(Number(perHaQty)) : ''}
                          onChange={(e) => {
                            const newPerHa = clampDecimals(parseFloat(e.target.value))
                            if (isNaN(newPerHa)) return
                            setEditForm(prev => {
                              const inputs = [...(prev.inputs || [])]
                              const entry = inputs[index]
                              if (entry && 'quantityPerHa' in entry) {
                                inputs[index] = {
                                  ...entry,
                                  quantityPerHa: newPerHa,
                                  totalQuantity: clampDecimals(newPerHa * lotAreaHa),
                                }
                              }
                              return { ...prev, inputs }
                            })
                          }}
                          className="w-full text-xs text-right bg-transparent border-b border-slate-300 dark:border-slate-600 focus:outline-none focus:border-emerald-500 text-gray-700 dark:text-gray-300 py-0.5"
                        />
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {perHaQty !== null ? Number(perHaQty).toFixed(2) : '-'}
                        </span>
                      )}
                    </div>
                    
                    {/* AGC: Remove Button */}
                    <div className="col-span-1 flex justify-end">
                      {canEdit && (
                        <button
                          onClick={() => handleRemoveInput(index)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm mb-3">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {t('tasks.noInputsAdded')}
            </div>
          )}
          
          {/* AGC: Add Input Section */}
          {canEdit && (
            <div className="border-t border-slate-200 dark:border-slate-600 pt-3">
              {/* AGC: Source Toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setNewInputEntry(prev => ({ ...prev, isCustom: false, inputId: '', customName: '' }))}
                  className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all bg-emerald-600 text-white border-emerald-600 shadow-sm"
                >
                  <Plus className="w-3 h-3 inline mr-1" />
                  {t('tasks.fromCatalog')}
                </button>
                <button
                  onClick={() => setShowNewInputModal(true)}
                  className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                >
                  <Plus className="w-3 h-3 inline mr-1" />
                  {t('tasks.newInput')}
                </button>
              </div>
              
              {/* AGC: Catalog Input Selection */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-600 p-3 space-y-2">
                <div className="relative">
                  <button
                    onClick={() => setShowInputDropdown(!showInputDropdown)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center justify-between hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {newInputEntry.inputId
                        ? availableInputs.find(i => i.id === newInputEntry.inputId)?.name
                        : t('tasks.selectInput')}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>

                  {showInputDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowInputDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto">
                        {isLoadingInputs ? (
                          <div className="p-3 text-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                          </div>
                        ) : availableInputs.length === 0 ? (
                          <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                            {t('tasks.noInputsAvailable')}
                          </div>
                        ) : (
                          availableInputs.map((input) => (
                            <button
                              key={input.id}
                              onClick={() => {
                                setNewInputEntry(prev => ({ ...prev, inputId: input.id }))
                                setShowInputDropdown(false)
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg flex items-center justify-between"
                            >
                              <div>
                                <span className="text-sm text-gray-900 dark:text-white">{input.name}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({input.unit || 'un'})</span>
                              </div>
                              {!input.isActive && (
                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-2">
                  <input
                    type="number"
                    value={newInputEntry.quantity}
                    onChange={(e) => setNewInputEntry(prev => ({ ...prev, quantity: e.target.value }))}
                    placeholder={t('tasks.totalQuantity')}
                    className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={handleAddInput}
                    disabled={!newInputEntry.inputId || !newInputEntry.quantity}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                  >
                    {t('common.add')}
                  </button>
                </div>

                {lotAreaHa > 0 && newInputEntry.quantity && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    ≈ {(parseFloat(newInputEntry.quantity) / lotAreaHa).toFixed(2)} {availableInputs.find(i => i.id === newInputEntry.inputId)?.unit || 'un'}/ha
                    {' '}({t('tasks.for')} {lotAreaHa.toFixed(1)} ha)
                  </p>
                )}
              </div>

              {/* AGC: Register New Product Modal — anchored to top for keyboard visibility */}
              {showNewInputModal && (
                <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-14">
                  <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl max-h-[90vh] overflow-y-auto mx-4">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <Plus className="w-5 h-5 text-emerald-600" />
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{t('tasks.registerNewProduct')}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('tasks.registerNewProductDesc')}</p>
                        </div>
                      </div>
                      <button onClick={() => setShowNewInputModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>

                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.itemName')} *</label>
                        <input
                          type="text"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Ex: Glifosato, Ureia..."
                          className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.category')}</label>
                        <select
                          value={registerForm.category}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        >
                          {INPUT_CATEGORIES.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.namePt}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.unit')}</label>
                        <select
                          value={registerForm.unit}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, unit: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        >
                          <option value="L">L</option>
                          <option value="kg">kg</option>
                          <option value="g">g</option>
                          <option value="mL">mL</option>
                          <option value="un">un</option>
                          <option value="sacas">sacas</option>
                          <option value="doses">doses</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.pricePerUnit')}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={registerForm.costPerUnit || ''}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, costPerUnit: parseFloat(e.target.value) || 0 }))}
                          placeholder="R$ 0,00"
                          className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.initialQuantity')}</label>
                          <input
                            type="number"
                            min="0"
                            value={registerForm.initialQuantity || ''}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, initialQuantity: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.minimumStockLabel')}</label>
                          <input
                            type="number"
                            min="0"
                            value={registerForm.minimumStock || ''}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, minimumStock: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => setShowNewInputModal(false)}
                          className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={handleRegisterNewInput}
                          disabled={!registerForm.name.trim() || isRegistering}
                          className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isRegistering ? t('tasks.registering') : t('tasks.register')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AGC: Register New Machine Modal — anchored to top for keyboard visibility */}
        {showNewMachineModal && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-14">
            <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl max-h-[90vh] overflow-y-auto mx-4">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Tractor className="w-5 h-5 text-orange-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{t('tasks.registerNewMachine')}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('tasks.registerNewMachineDesc')}</p>
                  </div>
                </div>
                <button onClick={() => setShowNewMachineModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.machineName')} *</label>
                  <input
                    type="text"
                    value={machineForm.name}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: JD 7755, Stara Imperador..."
                    className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.machineCategory')}</label>
                  <select
                    value={machineForm.category}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, category: e.target.value as MachineCategory, catalogId: '', brand: '' }))}
                    className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                  >
                    {MACHINE_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* AGC: Sub-type selector — shows catalog machines for the selected category */}
                {FARM_MACHINES.filter(m => m.category === machineForm.category).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.type')}</label>
                    <select
                      value={machineForm.catalogId}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, catalogId: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                    >
                      <option value="">{t('tasks.selectInput')}...</option>
                      {FARM_MACHINES.filter(m => m.category === machineForm.category).map(m => (
                        <option key={m.id} value={m.id}>{m.icon} {m.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* AGC: Brand with datalist autocomplete from MACHINE_BRANDS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.machineBrand')}</label>
                  <input
                    type="text"
                    list={`brands-${machineForm.category}`}
                    value={machineForm.brand}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, brand: e.target.value }))}
                    placeholder="Ex: John Deere, Case IH..."
                    className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                  />
                  <datalist id={`brands-${machineForm.category}`}>
                    {(MACHINE_BRANDS[machineForm.category] || []).map(b => (
                      <option key={b} value={b} />
                    ))}
                  </datalist>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.machineModel')}</label>
                    <input
                      type="text"
                      value={machineForm.model}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, model: e.target.value }))}
                      placeholder="Ex: 5075E"
                      className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.year')}</label>
                    <input
                      type="number"
                      value={machineForm.year}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, year: e.target.value }))}
                      placeholder="2024"
                      min="1970"
                      max="2030"
                      className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowNewMachineModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleRegisterNewMachine}
                    disabled={!machineForm.name.trim() || isRegisteringMachine}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegisteringMachine ? t('tasks.registering') : t('tasks.register')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Details Grid - Redesigned with consistent styling */}
        <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div className="space-y-4">
            {/* AGC: Status */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.status')}</p>
                <div className="relative">
                  <button
                    onClick={() => canUpdateStatus && setShowStatusDropdown(!showStatusDropdown)}
                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between ${canUpdateStatus ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                    disabled={!canUpdateStatus}
                  >
                    <span className={`px-2 py-0.5 rounded-full text-sm ${
                      statusOptions.find(s => s.value === editForm.status)?.color || ''
                    }`}>
                      {statusOptions.find(s => s.value === editForm.status)?.label || 'Selecionar'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  {showStatusDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setEditForm(prev => ({ ...prev, status: option.value }))
                              setShowStatusDropdown(false)
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <span className={`px-2 py-0.5 rounded-full text-sm ${option.color}`}>
                              {option.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* AGC: Due Date */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.dueDate')}</p>
                <input
                  type="date"
                  value={editForm.dueDate?.split('T')[0] || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  disabled={!canEdit}
                />
              </div>
            </div>

            {/* AGC: Priority */}
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                editForm.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30' :
                editForm.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'
              }`}>
                <Flag className={`w-4 h-4 ${
                  editForm.priority === 'high' ? 'text-red-600 dark:text-red-400' :
                  editForm.priority === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                }`} />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.priority')}</p>
                <div className="relative">
                  <button
                    onClick={() => canEdit && setShowPriorityDropdown(!showPriorityDropdown)}
                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between ${canEdit ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                    disabled={!canEdit}
                  >
                    <span className={`text-sm font-medium ${priorityOptions.find(p => p.value === editForm.priority)?.color || 'text-gray-600'}`}>
                      {priorityOptions.find(p => p.value === editForm.priority)?.label || 'Selecionar'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  {showPriorityDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowPriorityDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600">
                        {priorityOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setEditForm(prev => ({ ...prev, priority: option.value }))
                              setShowPriorityDropdown(false)
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                          >
                            <Flag className={`w-4 h-4 ${option.color}`} />
                            <span className="text-gray-900 dark:text-white">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* AGC: Category */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                {taskCategoryImg ? <img src={taskCategoryImg} alt="" className="w-5 h-5 object-contain" /> : <TaskCategoryIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.category')}</p>
                <div className="relative">
                  <button
                    onClick={() => canEdit && setShowCategoryDropdown(!showCategoryDropdown)}
                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between ${canEdit ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                    disabled={!canEdit}
                  >
                    <span className="text-sm text-gray-900 dark:text-white">
                      {TASK_CATEGORIES.find(c => c.id === editForm.category)?.name || 'Selecionar'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  {showCategoryDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCategoryDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 max-h-48 overflow-y-auto">
                        {TASK_CATEGORIES.map((cat) => {
                          const CatIcon = getTaskCategoryIcon(cat.id)
                          const catImg = taskIconImgMap[cat.id]
                          return (
                            <button
                              key={cat.id}
                              onClick={() => {
                                setEditForm(prev => ({ ...prev, category: cat.id }))
                                setShowCategoryDropdown(false)
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                            >
                              {catImg ? <img src={catImg} alt="" className="w-5 h-5 object-contain" /> : <CatIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                              <span className="text-gray-900 dark:text-white">{cat.name}</span>
                            </button>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* AGC: Lot/Field */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.plot')}</p>
                <div className="relative">
                  <button
                    onClick={() => canEdit && setShowLotDropdown(!showLotDropdown)}
                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between ${canEdit ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                    disabled={!canEdit}
                  >
                    <span className="text-sm truncate text-gray-900 dark:text-white">
                      {editForm.plotName || editForm.fieldName || 'Selecionar lote'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                  
                  {showLotDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowLotDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 max-h-48 overflow-y-auto">
                        {(lotes || []).map((lot) => (
                          <button
                            key={lot.id}
                            onClick={() => {
                              setEditForm(prev => ({ 
                                ...prev, 
                                plotId: lot.id,
                                plotName: lot.name,
                                fieldId: lot.fieldId,
                                fieldName: lot.name
                              }))
                              setShowLotDropdown(false)
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <span className="text-sm text-gray-900 dark:text-white">{lot.name}</span>
                            {lot.area && (
                              <span className="text-xs text-gray-400 ml-2">
                                {lot.area.toFixed(1)} ha
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* AGC: Assignee Dropdown */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks.assignTo')}</p>
                <div className="relative">
                  <button
                    onClick={() => canEdit && setShowAssigneeDropdown(!showAssigneeDropdown)}
                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between ${canEdit ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                    disabled={!canEdit}
                  >
                    <span className="text-sm text-gray-900 dark:text-white">
                      {selectedAssignee 
                        ? (selectedAssignee.isCurrentUser ? t('tasks.assignToMe') : selectedAssignee.displayName)
                        : (editForm.assignedTo || t('tasks.unassigned'))
                      }
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  {showAssigneeDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowAssigneeDropdown(false)} />
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 max-h-48 overflow-y-auto">
                        {/* AGC: Refresh button */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation()
                            console.log('🔄 AGC: Manual refresh of assignees list')
                            setIsLoadingAssignees(true)
                            try {
                              const assignees = await getAssignableUsers(user)
                              setAvailableAssignees(assignees)
                              console.log('✅ AGC: Assignees refreshed manually:', assignees.length)
                            } catch (error) {
                              console.error('❌ AGC: Error refreshing assignees:', error)
                            } finally {
                              setIsLoadingAssignees(false)
                            }
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 text-blue-600 dark:text-blue-400 text-sm font-medium border-b border-slate-200 dark:border-slate-600 flex items-center gap-2"
                        >
                          <Loader2 className={`w-4 h-4 ${isLoadingAssignees ? 'animate-spin' : ''}`} />
                          {t('common.refresh') || 'Actualizar lista'}
                        </button>
                        
                        {/* AGC: Unassigned option */}
                        <button
                          onClick={() => {
                            setEditForm(prev => ({ 
                              ...prev, 
                              assignedToId: null,
                              assignedTo: ''
                            }))
                            setShowAssigneeDropdown(false)
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 text-gray-500 italic border-b border-slate-200 dark:border-slate-600"
                        >
                          {t('tasks.unassigned')}
                        </button>
                        
                        {isLoadingAssignees ? (
                          <div className="p-3 text-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                          </div>
                        ) : (
                          <>
                            {availableAssignees.length === 0 && (
                              <div className="p-3 text-center text-gray-500 text-sm">
                                {t('tasks.noAssigneesAvailable') || 'No hay colaboradores disponibles'}
                              </div>
                            )}
                            {availableAssignees.map((assignee) => (
                            <button
                              key={assignee.id}
                              onClick={() => {
                                setEditForm(prev => ({ 
                                  ...prev, 
                                  assignedToId: assignee.uid,
                                  assignedTo: assignee.displayName
                                }))
                                setShowAssigneeDropdown(false)
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                              {/* AGC: Avatar or initials */}
                              <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-xs font-medium text-purple-600 dark:text-purple-400">
                                {assignee.avatar ? (
                                  <img src={assignee.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  assignee.displayName.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {assignee.isCurrentUser ? t('tasks.assignToMe') : assignee.displayName}
                                </span>
                                {assignee.subtype && (
                                  <span className="text-xs text-gray-400 ml-2">({assignee.subtype})</span>
                                )}
                              </div>
                            </button>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* AGC: T3 — Combo-aware Máquina picker. Replaces the flat v3.12.2 dropdown. */}
            {/* AGC: Rule is derived from the shared activityRules module; no local inline maps. */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0 mt-1">
                <Tractor className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('tasks.machine')}</p>
                {(() => {
                  const taskCat = String(editForm.category || task?.category || '').toLowerCase()
                  const rule = getRule(taskCat)

                  // AGC: Helper: pick a machine for a given slot index (persists to machinery[]).
                  const pickMachine = (slotIndex: number, machine: MachineInstance | null) => {
                    setEditForm(prev => {
                      const arr = [...(prev.machinery || [])]
                      if (machine) {
                        arr[slotIndex] = { id: machine.id, name: machine.name, category: machine.category }
                      } else {
                        arr.splice(slotIndex, 1)
                      }
                      // AGC: Remove trailing empty slots.
                      while (arr.length > 0 && !arr[arr.length - 1]) arr.pop()
                      return { ...prev, machinery: arr }
                    })
                    setOpenSlot(null)
                  }

                  // AGC: Helper: render a single machine slot dropdown.
                  const renderSlot = (
                    slotIdx: number,
                    filterCategory: MachineCategory | null,
                    label: string,
                  ) => {
                    const selected = (editForm.machinery || [])[slotIdx]
                    // AGC: v3.12.4 — normalize category before compare. Fleet data has
                    // AGC: mixed English/Portuguese (tractor/trator, sprayer/pulverizador);
                    // AGC: strict equality missed them all.
                    const candidates = filterCategory
                      ? userMachines.filter(m => normalizeMachineCategory(m.category) === filterCategory)
                      : userMachines
                    return (
                      <div key={slotIdx} className="mb-2">
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-1">{label}</p>
                        <div className="relative">
                          <button
                            onClick={() => canEdit && setOpenSlot(openSlot === slotIdx ? null : slotIdx)}
                            disabled={!canEdit}
                            className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg flex items-center justify-between text-sm ${canEdit ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'cursor-not-allowed opacity-75'}`}
                          >
                            <span className="truncate text-gray-900 dark:text-white">
                              {selected?.name || t('tasks.selectMachine')}
                            </span>
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </button>
                          {openSlot === slotIdx && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenSlot(null)} />
                              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 max-h-48 overflow-y-auto">
                                <button
                                  onClick={() => pickMachine(slotIdx, null)}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 text-gray-500 italic text-sm"
                                >
                                  {t('tasks.noMachine')}
                                </button>
                                {candidates.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-gray-500 italic">
                                    {t('tasks.noMachinesYet') || 'Nenhuma máquina cadastrada.'}
                                  </div>
                                ) : (
                                  candidates.map(m => (
                                    <button
                                      key={m.id}
                                      onClick={() => pickMachine(slotIdx, m)}
                                      className="w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                      <Tractor className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                      <span className="text-sm text-gray-900 dark:text-white flex-1 truncate">{m.name}</span>
                                      <span className="text-xs text-gray-400">{m.category}</span>
                                    </button>
                                  ))
                                )}
                                {/* AGC: Quick-register machine from task */}
                                <button
                                  onClick={() => { setOpenSlot(null); setShowNewMachineModal(true) }}
                                  className="w-full px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700"
                                >
                                  <Plus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                  <span className="text-sm text-emerald-600 font-medium">{t('tasks.newMachine')}</span>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  }

                  // AGC: Helper: yellow trator-fallback banner (shown when ideal category missing).
                  const tratorFallbackBanner = (idealCategory: string) => {
                    const hasIdeal = userMachines.some(m => normalizeMachineCategory(m.category) === idealCategory)
                    if (hasIdeal || !rule.tratorFallback) return null
                    return (
                      <div className="flex items-start gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2 mb-2 text-xs text-yellow-700 dark:text-yellow-300">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          {t('tasks.idealMissing', { category: idealCategory })
                            || `Ideal: ${idealCategory}. Usando trator como alternativa.`}
                        </span>
                      </div>
                    )
                  }

                  // AGC: Clear all machinery selections.
                  const clearAll = () => setEditForm(prev => ({ ...prev, machinery: [] }))

                  // AGC: Branch on rule.mode.
                  if (rule.mode === 'any') {
                    // AGC: any — single flat dropdown, no filter.
                    return (
                      <div>
                        {renderSlot(0, null, '')}
                        {(editForm.machinery || []).length > 0 && canEdit && (
                          <button onClick={clearAll} className="text-xs text-gray-400 hover:text-red-500 mt-1">
                            {t('tasks.noMachine')}
                          </button>
                        )}
                      </div>
                    )
                  }

                  if (rule.mode === 'standalone') {
                    // AGC: standalone — one slot, filtered to the single allowed category.
                    const idealCat = rule.allowedCombos[0]?.[0] ?? null
                    const hasIdeal = idealCat ? userMachines.some(m => normalizeMachineCategory(m.category) === idealCat) : false
                    const slotCat = (idealCat && hasIdeal) ? idealCat : (rule.tratorFallback ? 'trator' as MachineCategory : idealCat)
                    return (
                      <div>
                        {idealCat && tratorFallbackBanner(idealCat)}
                        {renderSlot(0, slotCat, idealCat ?? '')}
                      </div>
                    )
                  }

                  if (rule.mode === 'combo-required') {
                    // AGC: combo-required — two (or more) slots, one per category in allowedCombos[0].
                    // AGC: plantio: trator + plantadeira.
                    const combo = rule.allowedCombos[0] ?? []
                    const missingCats = combo.filter(cat => !userMachines.some(m => normalizeMachineCategory(m.category) === cat))
                    return (
                      <div>
                        {missingCats.length > 0 && (
                          <div className="flex items-start gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2 mb-2 text-xs text-yellow-700 dark:text-yellow-300">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                              {t('tasks.addImplementPrompt', { category: missingCats.join(', ') })
                                || `Falta ${missingCats.join(', ')}. Adicione em Maquinário.`}
                            </span>
                          </div>
                        )}
                        {combo.map((cat, idx) =>
                          renderSlot(idx, cat, cat)
                        )}
                      </div>
                    )
                  }

                  if (rule.mode === 'either') {
                    // AGC: either — radio toggle between autopropelido (combo[0]) and trator+implement (combo[1]).
                    // AGC: Phase 1 heuristic: all machines of the implement category count as self-propelled.
                    const selfCombo = rule.allowedCombos[0] ?? []   // AGC: e.g. ['pulverizador']
                    const tratorCombo = rule.allowedCombos[1] ?? [] // AGC: e.g. ['trator', 'pulverizador']
                    const implementCat = selfCombo[0] ?? null
                    const hasImplement = implementCat ? userMachines.some(m => normalizeMachineCategory(m.category) === implementCat) : false

                    return (
                      <div>
                        {/* AGC: Mode radio */}
                        <div className="flex gap-2 mb-3">
                          {[
                            { val: 'self-propelled' as const, label: t('tasks.pickSelfPropelled') || 'Autopropelido' },
                            { val: 'trator-implement' as const, label: t('tasks.pickTratorImplement') || 'Trator + implemento' },
                          ].map(opt => (
                            <button
                              key={opt.val}
                              onClick={() => canEdit && setMachineryMode(opt.val)}
                              className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                                machineryMode === opt.val
                                  ? 'bg-orange-600 text-white border-orange-600'
                                  : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-slate-300 dark:border-slate-600'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {machineryMode === 'self-propelled' && (
                          <div>
                            {!hasImplement && implementCat && tratorFallbackBanner(implementCat)}
                            {renderSlot(0, hasImplement ? implementCat : (rule.tratorFallback ? 'trator' as MachineCategory : implementCat), implementCat ?? '')}
                          </div>
                        )}
                        {machineryMode === 'trator-implement' && (
                          <div>
                            {tratorCombo.map((cat, idx) =>
                              renderSlot(idx, cat, cat)
                            )}
                          </div>
                        )}
                      </div>
                    )
                  }

                  // AGC: Fallback: plain dropdown for unknown modes.
                  return renderSlot(0, null, '')
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* AGC: Map Preview — pointer-events-none blocks touch/scroll capture */}
        {hasCoordinates && currentLot && (
          <div className="pointer-events-none">
            <LotMapPreviewCard
              mapData={{
                polygon: currentLot.coordinates,
                totalHectares: currentLot.area || 0,
              }}
              onPress={() => {}}
            />
          </div>
        )}

        {/* AGC: T1 — Inline Save/Cancel. Removed fixed footer so BottomNav is the single
            AGC: always-visible bottom element. pb-24 on the outer container gives breathing
            AGC: room above the BottomNav. */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="secondary"
            fullWidth
            onClick={handleCancel}
            icon={<X className="w-5 h-5" />}
          >
            {t('common.cancel')}
          </Button>
          {canEdit && (
            <Button
              fullWidth
              onClick={() => handleSave()}
              disabled={isSaving}
              icon={isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskDetail
